<template>
  <div class="contents">
    <div class="d-flex section-title-with-back">
      <img src="@/assets/images/Icon awesome-list-ul_bk.png" alt width="27.3" height="27.3" />
      <p class="title-bk">生徒の所属を登録</p>
    </div>
    <div class="container">
      <label>チェックした生徒に登録する情報を選択してください</label>
      <ValidationObserver v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(confirmEdited)" ref="form">
          <table class="table table-form">
            <tbody>
              <tr>
                <th scope="row">読み込んだ生徒数：</th>
                <td />
                <td>{{ totalStudents }}</td>
              </tr>
              <tr>
                <th scope="row">現在チェックが入っている生徒数：</th>
                <td />
                <td>{{ totalSelectedStudents }}</td>
              </tr>
              <br />
              <ChoiceField
                v-model="corporationChoiceFieldMultiModels"
                extraRules="required_select"
                label="法人名（法人ID）"
                placeholder="選択して下さい"
                resourceType="corporation"
                modelFieldName="corporation"
                :choiceDisable="loggedInAsCorporation || loggedInAsBrand || loggedInAsSchool"
                :childFieldNames="['brand', 'school', 'course', 'tamaiClass']"
                :formData="currentGroupStudent"
              />
              <ChoiceField
                v-model="brandChoiceFieldMultiModels"
                extraRules="required_select"
                label="ブランド名（ブランドID）"
                placeholder="選択して下さい"
                resourceType="brand"
                modelFieldName="brand"
                :choiceDisable="loggedInAsBrand || loggedInAsSchool || isEmpty(currentGroupStudent.corporation)"
                :childFieldNames="['school', 'course', 'tamaiClass']"
                :formData="currentGroupStudent"
              />
              <ChoiceField
                v-model="schoolChoiceFieldMultiModels"
                extraRules="required_select"
                label="教場名（教場ID）"
                placeholder="選択して下さい"
                resourceType="school"
                modelFieldName="school"
                :choiceDisable="loggedInAsSchool || isEmpty(currentGroupStudent.brand)"
                :childFieldNames="['tamaiClass']"
                :formData="currentGroupStudent"
              />
              <ChoiceField
                v-model="courseChoiceFieldMultiModels"
                extraRules="required_select"
                label="コース名（コースID）"
                placeholder="選択して下さい"
                resourceType="course"
                modelFieldName="course"
                :choiceDisable="isEmpty(currentGroupStudent.brand)"
                :childFieldNames="['tamaiClass']"
                :formData="currentGroupStudent"
              />
              <ChoiceField
                v-model="tamaiClassChoiceFieldMultiModels"
                extraRules="required_select"
                label="クラス名（クラスID）"
                placeholder="選択して下さい"
                resourceType="class"
                modelFieldName="tamaiClass"
                :choiceDisable="isEmpty(currentGroupStudent.school) || isEmpty(currentGroupStudent.course)"
                :formData="currentGroupStudent"
              />
            </tbody>
          </table>
          <div class="text-center">
            <button type="submit" class="btn-confirm">登録</button>
          </div>
        </form>
      </ValidationObserver>
      <PopUpHolder
        ref="popup_holder"
        no-close-on-esc
        no-close-on-backdrop
        green-button-title="リストに戻る"
        @greenClick="popupConfirmed"
      >
        <template v-slot:header>生徒所属登録</template>
        <template v-slot:body>チェックされた生徒に所属を紐付けました</template>
      </PopUpHolder>
      <PopUpHolder
        ref="not_included"
        no-close-on-esc
        no-close-on-backdrop
        green-button-title="閉じる"
        @greenClick="notIncludedConfirmed"
      >
        <template v-slot:header>エラー</template>
        <template v-slot:body
          >生徒の利用教材が法人の利用教材と一致しません。<br />
          法人もしくは生徒の利用教材を確認してください。
        </template>
      </PopUpHolder>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { ValidationObserver } from 'vee-validate';
import ChoiceField from '@/components/forms/ChoiceField.vue';
import PopUpHolder from '@/components/popups/PopUpHolder.vue';
import {
  basicCorporationNameIdPresent,
  basicBrandNameIdPresent,
  basicSchoolNameIdPresent,
  basicCourseNameIdPresent,
  basicTamaiClassNameIdPresent,
} from '@/presenters/fields';
import isEmpty from 'lodash/isEmpty';

export default {
  components: {
    ValidationObserver,
    ChoiceField,
    PopUpHolder,
  },
  mounted() {
    if (this.loggedInAsCorporation) {
      this.setCorporation(this.corporation.corporation);
    }
    if (this.loggedInAsBrand) {
      this.setBrand(this.brand.brand);
      this.setCorporation(this.brand.brand.corporation);
    }
    if (this.loggedInAsSchool) {
      this.setSchool(this.school.school);
      this.setBrand(this.school.school.brand);
      this.setCorporation(this.school.school.corporation);
    }
  },
  computed: {
    currentGroupStudent() {
      return this.student_importer.groupStudent;
    },
    corporationChoiceFieldMultiModels() {
      return basicCorporationNameIdPresent(this.currentGroupStudent.corporation);
    },
    brandChoiceFieldMultiModels() {
      return basicBrandNameIdPresent(this.currentGroupStudent.brand);
    },
    schoolChoiceFieldMultiModels() {
      return basicSchoolNameIdPresent(this.currentGroupStudent.school);
    },
    courseChoiceFieldMultiModels() {
      return basicCourseNameIdPresent(this.currentGroupStudent.course);
    },
    tamaiClassChoiceFieldMultiModels() {
      return basicTamaiClassNameIdPresent(this.currentGroupStudent.tamaiClass);
    },
    ...mapState(['student_importer']),
    ...mapState(['corporation']),
    ...mapState(['brand']),
    ...mapState(['school']),
    ...mapState({
      teachingMaterials: (state) => state.teaching_material.teachingMaterials,
    }),
    ...mapGetters('student_importer', ['totalStudents', 'totalSelectedStudents']),
    ...mapGetters('auth', ['loggedInAsCorporation', 'loggedInAsBrand', 'loggedInAsSchool']),
  },
  methods: {
    includesStudentProductsInCorporationProducts() {
      const selectedStudents = this.student_importer.groupStudent.selectedStudents;
      const studentTeachingMaterialCodes = selectedStudents.map((student) =>
        student.figuredTeachingMaterialCodes.join('_')
      );
      const joinedCodes = studentTeachingMaterialCodes.join('_');
      const splitedCodes = joinedCodes.split('_');
      const uniqueStudentTeachingMaterialCodes = Array.from(new Set(splitedCodes)); // ["200", "300", "400"]
      const studentProductCodes = uniqueStudentTeachingMaterialCodes.map((studentTeachingMaterialCode) => {
        const teachingMaterial = this.teachingMaterials.find((teachingMaterial) => {
          return teachingMaterial.code === studentTeachingMaterialCode;
        });
        return teachingMaterial.productCode;
      });

      const corporationProducts = this.student_importer.groupStudent.corporation.figuredProducts;
      for (const studentProductCode of studentProductCodes) {
        const matchedProduct = corporationProducts.find((corporationProduct) => {
          return corporationProduct.code === studentProductCode;
        });
        if (isEmpty(matchedProduct)) {
          return false;
        }
      }
      return true;
    },
    confirmEdited() {
      if (this.includesStudentProductsInCorporationProducts()) {
        this.finishEditingGroupStudents();
        this.$refs['popup_holder'].show();
      } else {
        this.$logger.debug('Display not included Error');
        this.$refs['not_included'].show();
      }
    },
    popupConfirmed() {
      this.$router.push({ name: 'student-import' });
    },
    notIncludedConfirmed() {
      this.$logger.debug('Not Included Confirmed Popup done');
    },
    ...mapActions('student_importer', ['finishEditingGroupStudents', 'setCorporation', 'setBrand', 'setSchool']),
  },
};
</script>

<style scoped>
.btn-confirm {
  margin-left: 0px;
}
.table-form {
  margin-left: 20px;
  margin-top: 20px;
  width: 95%;
}
</style>
